// ============================================
// FILE: src/utils/variantHelpers.js
// ============================================
//
// Resolves an option selection ({color: 'navy-blue', size: 'm'}) to a real
// variant, and answers "is this value still reachable?" as the shopper clicks.
//
// The combo key rule mirrors the backend exactly (ProductVariant::$combo_key):
// option codes sorted alphabetically, "code:value" joined by "|". Change it in
// one place and the matrix lookup silently returns nothing.

import { getFinalPrice } from './priceHelpers';

/** "color:navy-blue|size:m" — codes sorted alphabetically. */
export const optionComboKey = (selection = {}) =>
  Object.keys(selection)
    .filter((code) => selection[code] != null && selection[code] !== '')
    .sort()
    .map((code) => `${code}:${selection[code]}`)
    .join('|');

/** Map of combo key -> matrix entry, for O(1) lookups. */
export const buildMatrixIndex = (variantMatrix = []) => {
  const index = new Map();
  (Array.isArray(variantMatrix) ? variantMatrix : []).forEach((entry) => {
    if (entry?.key != null) index.set(entry.key, entry);
  });
  return index;
};

const getMatrix = (product) =>
  Array.isArray(product?.variant_matrix) ? product.variant_matrix : [];

/**
 * The option axes for this product.
 *
 * Synthesises them from the legacy `variants[].attributes` shape when the
 * backend did not send `options`, so this works against an older API too.
 */
export const getOptions = (product) => {
  if (Array.isArray(product?.options) && product.options.length > 0) {
    return product.options;
  }

  const variants = (Array.isArray(product?.variants) ? product.variants : [])
    .filter((v) => v && (v.status === undefined || v.status === 'active'));

  if (variants.length === 0) return [];

  const axes = new Map();

  variants.forEach((variant) => {
    const attrs = variant.options || variant.attributes;
    if (!attrs || typeof attrs !== 'object') return;

    Object.entries(attrs).forEach(([code, value]) => {
      if (value == null || value === '') return;
      if (!axes.has(code)) axes.set(code, new Map());

      const values = axes.get(code);
      if (!values.has(value)) {
        values.set(value, {
          id: `${code}:${value}`,
          value,
          label: String(value),
          hex: null,
          image_url: null,
          available: false,
        });
      }
      if (variant.is_in_stock) values.get(value).available = true;
    });
  });

  return Array.from(axes.entries()).map(([code, values]) => ({
    code,
    label: code.charAt(0).toUpperCase() + code.slice(1),
    type: code === 'color' ? 'swatch' : 'pill',
    values: Array.from(values.values()),
  }));
};

/** The matrix entry for a complete selection, or null. */
export const findVariant = (product, selection = {}) => {
  const matrix = getMatrix(product);
  if (matrix.length === 0) return null;

  const key = optionComboKey(selection);
  if (!key) return null;

  return matrix.find((entry) => entry.key === key) || null;
};

/**
 * Contextual availability: can `value` still be chosen for `code`, given
 * everything ELSE already selected?
 *
 * This is the difference between the API's `options[].values[].available`
 * (global — some variant somewhere has it) and what the shopper needs to see
 * (XL greys out once Olive is picked, because there is no Olive XL in stock).
 */
export const isValueAvailable = (product, code, value, selection = {}) => {
  const matrix = getMatrix(product);

  if (matrix.length === 0) {
    // No matrix to reason about — fall back to the global flag.
    const option = getOptions(product).find((o) => o.code === code);
    const entry = option?.values.find((v) => String(v.value) === String(value));
    return entry ? entry.available !== false : true;
  }

  const others = { ...selection };
  delete others[code];

  return matrix.some((row) => {
    if (String(row.options?.[code]) !== String(value)) return false;
    if (!row.is_in_stock) return false;

    return Object.entries(others).every(
      ([otherCode, otherValue]) =>
        otherValue == null ||
        otherValue === '' ||
        String(row.options?.[otherCode]) === String(otherValue)
    );
  });
};

/**
 * Opening selection: the flagged default variant, else the first in-stock one,
 * else the first entry. Returns {} for a product with no options.
 */
export const getDefaultSelection = (product) => {
  const matrix = getMatrix(product);
  if (matrix.length === 0) return {};

  const preferred =
    matrix.find((entry) => entry.is_default && entry.is_in_stock) ||
    matrix.find((entry) => entry.is_default) ||
    matrix.find((entry) => entry.is_in_stock) ||
    matrix[0];

  return preferred?.options ? { ...preferred.options } : {};
};

/** True once every axis the product offers has a value chosen. */
export const isSelectionComplete = (product, selection = {}) => {
  const options = getOptions(product);
  if (options.length === 0) return true;

  return options.every((option) => {
    const chosen = selection[option.code];
    return chosen != null && chosen !== '';
  });
};

/**
 * The payload cartHelpers.addToCart expects.
 *
 * Shape-identical to what ProductDetailsModal already produces, which is why
 * neither cartHelpers nor the backend CartController needs any change: the
 * cart line key stays `${id}::${variant_id}` and the POST body stays
 * {product_id, variant_id, quantity, price}.
 */
export const buildCartPayload = (product, entry) => {
  if (!entry) return product;

  return {
    ...product,
    variant_id: entry.variant_id,
    variant_name: entry.name,
    price: entry.price != null ? entry.price : product.price,
    sale_price: entry.sale_price != null ? entry.sale_price : null,
    final_price: entry.final_price != null ? entry.final_price : getFinalPrice(product),
  };
};

/**
 * Price to display for the current state: the selected variant's price when
 * one resolves, otherwise the product's own.
 */
export const getSelectionPrice = (product, entry) =>
  entry?.final_price != null ? entry.final_price : getFinalPrice(product);
